import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import moment from 'moment';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { CorrespondantCourrier } from 'src/app/utils/interfaces/new/correspondant-courrier.interface';
import { Courrier } from 'src/app/utils/interfaces/new/Courrier';
import { CreateCourrier } from 'src/app/utils/interfaces/new/create-courrier.interface';
import { FichePatient } from 'src/app/utils/interfaces/new/fiche-patient.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../../filtres/filtres.service';
import { HeaderFooterService } from '../../header-footer/header-footer.service';
import { InsDatamatrixService } from '../../ins/ins-datamatrix.service';
import { SnackbarService } from '../../snackbar/snackbar.service';
import { QuillUtilsService } from '../../quill-utils/quill-utils.service';
import { LoginService } from '../../login/login.service';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { Annexe } from 'src/app/fragments/modal/edit-courrier/edit-courrier.fragment';
import { SafeHtmlUtils } from 'src/app/utils/safe-html.util';

const DOCUMENT_MAIL_PATH = '/consultation/';

export interface CourrierDestinataires {
  to?: Array<string>;
  cc?: Array<string>;
  bcc?: Array<string>;
}

@Injectable({
  providedIn: 'root'
})
export class CourriersService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private basePath = environment.basePath + DOCUMENT_MAIL_PATH;

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService,
    private _headFootSrv: HeaderFooterService,
    private _insDatamatrixSrv: InsDatamatrixService,
    private _loginSrv: LoginService
  ) { }

  public async create(courrier: CreateCourrier, consultId: string, annexes?: string[]): Promise<Courrier> {
    try {
      let body: any;
      if (annexes) {
        body = { ...courrier, annexes };
      } else {
        body = courrier;
      }
      const res = await this._http.post(this.basePath + "courrier/consultation/" + consultId, body, this.httpOptions).toPromise();
      const result = res.body as Courrier;
      return result;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async find(consultId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'modificationDate',
    sortOrder: SortEnum.DESC
  }): Promise<Paginated<Courrier>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(this.basePath + "courrier/consultation/" + consultId, this.httpOptions).toPromise();
      const courriers = res.body as Courrier[];
      const paginated: Paginated<Courrier> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: courriers
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOne(courrierId: string): Promise<Courrier> {
    try {
      const res = await this._http.get(this.basePath + "courrier/" + courrierId, this.httpOptions).toPromise();
      const courrier = res.body as Courrier;
      return courrier;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async update(courrier: CreateCourrier, courrierId: string, annexes?: string[]) {
    try {
      let body: any;
      if (annexes) {
        body = { ...courrier, annexes };
      } else {
        body = courrier;
      }

      const res = await this._http.put(this.basePath + "courrier/" + courrierId, body, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async print(courrierId: string): Promise<string> {
    try {
      const res = await this._http.get(this.basePath + "courrier/" + courrierId + "/print", this.httpOptions).toPromise();
      const result = res.body as string;
      return result;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async searchCourriers(
    patientId: string,
    title?: string,
    dateStart?: Date,
    dateEnd?: Date,
    filtre: Filtres = {
      perpage: -1,
      currentPage: 1,
    }
  ): Promise<Paginated<Courrier>> {
    return new Promise<Paginated<Courrier>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);

        const query = {};

        if (title && title !== '') {
          query['title'] = title;
        }

        if (dateStart) {
          query['dateStart'] = encodeURIComponent(moment.utc(dateStart).toISOString());
        }

        if (dateEnd) {
          query['dateEnd'] = encodeURIComponent(moment.utc(dateEnd).toISOString());
        }

        this.httpOptions.params = query;

        const res = await this._http.get(`${this.basePath}courrier/patient/${patientId}`, this.httpOptions).toPromise();
        const mails = res.body as Array<Courrier>;
        const paginated: Paginated<Courrier> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: mails
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async deleteCourrier(courrierId): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        this.httpOptions.headers = undefined;

        const res = await this._http.delete(`${this.basePath}courrier/${courrierId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  async findCorrespondantsByCourrier(courrierId: string): Promise<CorrespondantCourrier[]> {
    try {
      const res = await this._http.get(this.basePath + "courrier/correspondants/" + courrierId, this.httpOptions).toPromise();
      const diag = res.body as CorrespondantCourrier[];
      return diag;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async addCorrespondantByCourrier(courrierId: string, correspondantId: string): Promise<any> {
    try {
      const res = await this._http.put(this.basePath + "courrier/correspondants/" + courrierId + "/" + correspondantId, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async deleteCorrespondantByCourrier(courrierId: string, correspondantId: string): Promise<any> {
    try {
      const res = await this._http.delete(this.basePath + "courrier/correspondants/" + courrierId + "/" + correspondantId, this.httpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async convertToPdf(courrier: CreateCourrier, destinaitaires?: CourrierDestinataires, patient?: FichePatient): Promise<string> {
    const _getInsType = (oid: string): string => {
      switch (oid) {
        case '1.2.250.1.213.1.4.8':
          return 'INS-NIR';
        case '1.2.250.1.213.1.4.9':
          return 'INS-NIA';
        case '1.2.250.1.213.1.4.10':
          return 'INS-NIR de test';
        case '1.2.250.1.213.1.4.11':
          return 'INS-NIR de démonstration';
      }
    };

    const _getSexeLibelle = (sexe: string): string => {
      switch (sexe.toUpperCase()) {
        case 'F':
        case 'FEMME':
          return 'féminin';
        case 'M':
        case 'H':
        case 'HOMME':
          return 'masculin';
        default:
          return 'inconnu';
      }
    };

    const _addInsCartouche = async (c: CreateCourrier): Promise<CreateCourrier> => {
      if (patient && patient.identiteStatutId === '95a6081f-5260-44b8-9661-e4795e1cfcaf') {
        const datamatrix = await this._insDatamatrixSrv.getPatientDatamatrix(patient.id);
        if (datamatrix) {
          const wrapperStyle = [
            'width: 90%',
            'margin-top: 50px',
            'margin: auto',
            'display: flex',
            'justify-content: space-between',
            'align-items: center',
          ];
          const infosWrapperStyle = [
            'display: flex',
            'flex-direction: column',
          ];

          const cartouche = `
            <div style="${wrapperStyle.join(';')}">
              <div style="${infosWrapperStyle.join(';')}">
                <span>${patient.nom} ${patient.prenomUsage && patient.prenomUsage !== '' ? patient.prenomUsage : patient.prenom} (sexe ${_getSexeLibelle(patient.sexe)})</span>
                <span>Né(e) le ${moment(patient.dateNaissance, 'YYYY-MM-DD').format('DD-MM-YYYY')} ${patient.cpNaissance && patient.cpNaissance !== '' ? '(' + patient.cpNaissance + ')' : ''}</span>
                <span>${_getInsType(patient.oid)} : ${patient.ins} ${patient.insKey}</span>
              </div>

              <div>
                <img src="${datamatrix.prefix}${datamatrix.base64}">
              </div>
            </div>
          `;

          c.courrierText = `${c.courrierText} ${cartouche}`;
        }
      }

      return c;
    };

    const _getCourrierFullContent = async (c: CreateCourrier): Promise<string> => {
      let header = '';
      let footer = '';
      let beforePrintContents = '';
      
      const escapeHtml = (unsafe: string) => {
        return (unsafe || '').replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
      };

      if (!c.isEnteteVide) {
        const userHeader = await this._headFootSrv.findOneHeader();
        if (userHeader) {
          header = SafeHtmlUtils.sanitizeHtmlString(userHeader.texte) + '<br><br>';
        }
      }
      
      if (c.isVilleDate) {
        const currentProfil = this._loginSrv.getCurrentProfil();
        if (currentProfil.cabinet) {
          beforePrintContents += '<p style="margin-top:10px;">A ' + escapeHtml(currentProfil.cabinet.ville) +' le '+ moment().format('DD/MM/YYYY') +'</p>';
        } else {
          beforePrintContents += '<p>Le : ' + moment().format('DD/MM/YYYY') + '</p>';
        }
      }
      
      if (c.isNomPatient && patient) {
        beforePrintContents += '<p>Nom et prénom du patient : ' + escapeHtml(patient.nom) +' '+ escapeHtml(patient.prenom ? patient.prenom : '') + '</p>';
      }

      if (!c.isPiedVide) {
        const userFooter = await this._headFootSrv.findOneFooter();
        if (userFooter) {
          footer = SafeHtmlUtils.sanitizeHtmlString(userFooter.texte);
        }
      }

      if (beforePrintContents !== '') {
        beforePrintContents += '<br><br>';
      }

      const safeCourrierText = SafeHtmlUtils.sanitizeHtmlString(c.courrierText || '');
      return `${header}${beforePrintContents}${safeCourrierText}${footer}`;
    };

    const _getRawHtmlContent = (c: string) => {
      const customPrintStyle = `
        @page {
            size: A4;
            margin-left:10mm;
            margin-right:10mm;
            margin-top:10mm;
            margin-bottom:10mm;
        }
      `;
      let rtd = `<html>
      <head>
                <style>
                    ${QuillUtilsService.setQuillPreviewStyle(QuillUtilsService.resetStyle)}
                    ${customPrintStyle}
                </style>
                <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
                <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
                <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
                <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>
            </head>
        <body class="fr-view">
              ${c}
        </body>
      </html>`;
      rtd = rtd.replace(/<br>/g, '<br/>');
      return rtd;
    };

    const _getHtmlContent = (s: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(s, 'text/html');
      doc.querySelectorAll('style').forEach(style => style.remove()); // On enlève les balises contenant le style Quill reçues avc le courrier
      doc.querySelectorAll('head').forEach(head => head.remove());
      doc.querySelectorAll('link').forEach(link => link.remove());

      return doc.documentElement.outerHTML;
      
    };

    const _htmlContentToBase64 = async (hc: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '790px';
        iframe.style.height = '1122px';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) {
          document.body.removeChild(iframe);
          return reject("Impossible de créer l'iframe pour le rendu PDF");
        }

        doc.open();
        doc.write(hc);
        doc.close();

        let attempts = 0;
        const checkReady = setInterval(async () => {
          attempts++;
          const pagedPages = doc.querySelectorAll('.pagedjs_page');
          if (pagedPages.length > 0 || attempts > 50) { 
            clearInterval(checkReady);
            
            setTimeout(async () => {
              try {
                const docJSPDF = new jsPDF({
                  orientation: 'p',
                  format: 'a4',
                  unit: 'px',
                  hotfixes: ['px_scaling'],
                });
                
                const pdfRootId = `pdf-${Math.random().toString(36).substring(7)}`;
                doc.documentElement.setAttribute('data-pdf-root', pdfRootId);
                
                await docJSPDF.html(doc.documentElement, {
                  autoPaging: 'text',
                  margin: [10, 0, 10, 0],
                  width: 790,
                  windowWidth: 790,
                  html2canvas: {
                    backgroundColor: 'white',
                    svgRendering: true,
                    letterRendering: true,
                    scale: 0.98,
                    onclone: (clonedDocument: Document) => {
                      const root = clonedDocument.querySelector<HTMLElement>(
                        `[data-pdf-root="${pdfRootId}"]`
                      );
          
                      if (!root) {
                        return;
                      }
                      
                      const style = clonedDocument.createElement('style');
                      style.innerHTML = `
                        [data-pdf-root="${pdfRootId}"] .fr-view {
                          font-size: 13px !important;
                          line-height: 1.3 !important;
                        }
                        [data-pdf-root="${pdfRootId}"] .fr-view p, 
                        [data-pdf-root="${pdfRootId}"] .fr-view span, 
                        [data-pdf-root="${pdfRootId}"] .fr-view li, 
                        [data-pdf-root="${pdfRootId}"] .fr-view td, 
                        [data-pdf-root="${pdfRootId}"] .fr-view th, 
                        [data-pdf-root="${pdfRootId}"] .fr-view div {
                          font-size: 13px !important;
                          line-height: 1.3 !important;
                        }
                        [data-pdf-root="${pdfRootId}"] .fr-view h1 { font-size: 18px !important; }
                        [data-pdf-root="${pdfRootId}"] .fr-view h2 { font-size: 16px !important; }
                        [data-pdf-root="${pdfRootId}"] .fr-view h3, 
                        [data-pdf-root="${pdfRootId}"] .fr-view h4, 
                        [data-pdf-root="${pdfRootId}"] .fr-view h5, 
                        [data-pdf-root="${pdfRootId}"] .fr-view h6 { font-size: 14px !important; }
                      `;
                      root.prepend(style);
                    },
                  },
                });
                
                const b64 = docJSPDF.output('datauristring');
                document.body.removeChild(iframe);
                resolve(b64);
              } catch (e) {
                document.body.removeChild(iframe);
                reject(e);
              }
            }, 500);
          }
        }, 100);
      });
    };

    const _cleanBase64 = (b64: string): string => {
      const split = b64.split('filename=generated.pdf;');
      if (split.length > 1) {
        return `${split[0]}${split[1]}`;
      } else {
        return `${split[0]}`;
      }
    };

    const contentWithCartouche = await _addInsCartouche(courrier);
    const fullContent = await _getCourrierFullContent(contentWithCartouche);
    const htmlContent = _getHtmlContent(fullContent);
    const rawContent = _getRawHtmlContent(htmlContent);
    const base64 = await _htmlContentToBase64(rawContent);
    const cleanBase64 = _cleanBase64(base64);
    return cleanBase64;
  }

}
